import { Heart, Eye, ShoppingBag, Star } from "lucide-react";
import { motion } from "framer-motion";
import { Link, useRouter } from "@tanstack/react-router";
import type { Product } from "@/lib/products";
import { useCart } from "@/lib/store";

type Props = {
  product: Product;
  wishlisted: boolean;
  onWishlist: () => void;
  onQuickView: () => void;
};

export function ProductCard({ product, wishlisted, onWishlist, onQuickView }: Props) {
  const { add } = useCart();
  const router = useRouter();
  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  return (
    <motion.div
      className="group bg-card rounded-[20px] overflow-hidden shadow-luxe transition-shadow duration-500 ring-1 ring-transparent hover:ring-gold/40 hover:shadow-[0_25px_60px_-20px_rgba(200,161,101,0.35)]"
      whileHover={{ y: -10, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 220, damping: 22 }}
    >
      <Link
        to="/shop/$productId"
        params={{ productId: product.id }}
        className="relative block aspect-[4/5] overflow-hidden bg-secondary"
        aria-label={`View ${product.name}`}
      >
        <img src={product.img} alt={product.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />

        {product.tag && (
          <span className="absolute top-4 left-4 bg-cream/95 backdrop-blur text-[0.6rem] tracking-[0.25em] uppercase px-3 py-1.5 rounded-full font-medium">
            {product.tag}
          </span>
        )}
        {!product.inStock && (
          <span className="absolute top-4 left-4 bg-primary text-cream text-[0.6rem] tracking-[0.25em] uppercase px-3 py-1.5 rounded-full font-medium">
            Sold Out
          </span>
        )}

        <div className="absolute top-2 right-2 sm:top-4 sm:right-4 flex flex-col gap-2 sm:opacity-0 sm:translate-x-3 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500">
          <motion.button
            aria-label="Wishlist"
            onClick={(e) => { stop(e); onWishlist(); }}
            whileTap={{ scale: 0.85 }}
            animate={wishlisted ? { scale: [1, 1.25, 1] } : { scale: 1 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className={`h-10 w-10 sm:h-10 sm:w-10 rounded-full grid place-items-center backdrop-blur transition-colors ${wishlisted ? "bg-gold text-primary" : "bg-cream/95 hover:bg-gold"}`}
          >
            <Heart className="h-4 w-4" strokeWidth={1.6} fill={wishlisted ? "currentColor" : "none"} />
          </motion.button>
          <button
            aria-label="Quick view"
            onClick={(e) => { stop(e); onQuickView(); }}
            className="h-10 w-10 sm:h-10 sm:w-10 rounded-full grid place-items-center bg-cream/95 backdrop-blur hover:bg-gold transition-colors"
          >
            <Eye className="h-4 w-4" strokeWidth={1.6} />
          </button>
        </div>

        <button
          disabled={!product.inStock}
          onClick={(e) => { stop(e); if (product.inStock && add(product.id)) { router.navigate({ to: "/cart" }); } }}
          className="hidden sm:flex absolute inset-x-4 bottom-4 translate-y-16 group-hover:translate-y-0 transition-transform duration-500 btn-gold justify-center disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <ShoppingBag className="h-4 w-4" /> {product.inStock ? "Add to Cart" : "Notify Me"}
        </button>
      </Link>

      <Link to="/shop/$productId" params={{ productId: product.id }} className="block p-3 sm:p-5">
        <div className="text-[0.65rem] tracking-[0.25em] uppercase text-gold">{product.category}</div>
        <h3 className="font-display text-base sm:text-xl mt-1 sm:mt-1.5 truncate">{product.name}</h3>
        <div className="mt-1.5 sm:mt-2 flex items-center gap-1 text-gold">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${i < Math.round(product.rating) ? "fill-current" : "opacity-30"}`} strokeWidth={0} />
          ))}
          <span className="text-[0.65rem] sm:text-[0.7rem] text-muted-foreground ml-1">({product.reviews})</span>
        </div>
        <div className="mt-1.5 sm:mt-2 flex flex-wrap items-baseline gap-1.5 sm:gap-2">
          <span className="text-sm sm:text-lg font-medium">₹{product.price}</span>
          {product.compareAt && <span className="text-xs sm:text-sm text-muted-foreground line-through">₹{product.compareAt}</span>}
        </div>
        <button
          disabled={!product.inStock}
          onClick={(e) => { stop(e); if (product.inStock && add(product.id)) { router.navigate({ to: "/cart" }); } }}
          className="sm:hidden mt-2.5 w-full h-11 rounded-full bg-primary text-cream text-[0.7rem] tracking-[0.2em] uppercase inline-flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform disabled:opacity-60"
        >
          <ShoppingBag className="h-3.5 w-3.5" /> {product.inStock ? "Add" : "Sold out"}
        </button>
      </Link>
    </motion.div>
  );
}
